import React, {
  memo,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { type NodeProps } from '@xyflow/react';
import './style.css';
import {
  InputValue,
  NodeCategory,
  NodeData,
  NodeInputType,
  NodeParam,
  NodeResult,
} from '@/types/node-types';
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { findCatalogStruct } from '@/stores/helpers/node-catalog-lookup';
import { useShallow } from 'zustand/react/shallow';
import { FloatingControlBar } from './floating-control-bar';
import { NodeHandle } from './node-handle';
import { getCategoryConfig } from '@/constants/node-styling';
import { SlotCard } from './slot-card';
import { PreviewSlotCard } from './preview-slot-card';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { resultProducesArtifact } from '@/pages/workflow/helper/artifact-node-manager';
import { TriangleAlert, Check, Database, Pause, Play } from 'lucide-react';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { LoadingSpinner } from '@/components/loading-spinner';
import { cn } from '@/lib/utils';
import { NodeEventType, WorkflowEventTypes } from '@/types/workflow-types';
import { websocketService } from '@/services/websocket/websocket-service';
import { ConnectionType } from '@/types/edge-types';
import { NodeHeader } from './node-header';
import { refreshFileUrls } from '@/services/workflow/get-file';
import { useOrgStore } from '@/stores/org-store';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useNodeActions } from '@/pages/workflow/contexts/node-actions-context';
import * as Sentry from '@sentry/react';
import { CollectionScopeCard } from './collection-scope-card';
import { INLINE_COLLECTION_FIELD_IDS } from '@/constants/collection-scope';
import { LiveTranscriptionPanel } from '@/pages/workflow/component/custom-node/live-transcription-panel';
import { AgentCapabilityChips } from './agent-capability-chips';

const GenericNode: React.FC<NodeProps> = (props) => {
  const { data, id, selected: isSelected = false } = props;
  const {
    currentWorkflow,
    currentSession,
    persistStatusHighlight,
    interventionState,
    setInterventionState,
    clearInterventionState,
  } = useWorkflowStore();
  const { fieldErrors, unsupportedNodes } = useWorkflowValidationStore();
  const { updateAvailableNodes } = useNodeStore(
    useShallow((s) => ({ updateAvailableNodes: s.updateAvailableNodes })),
  );
  const isDisabledNode = unsupportedNodes.some((n) => n.nodeId === id);
  const updateAvailable = updateAvailableNodes.some((n) => n.nodeId === id);
  const {
    openWorkflowPanel,
    showPreview,
    setActiveTab,
    setFilteredNodeId,
    setMaximized,
    setConsolePanelOpen,
    showFilesPreview,
    updateFilesPreview,
  } = useWorkflowPanelStore();
  const { currentOrg } = useOrgStore();
  const { isCanvasA2aChatEnabled } = useLaunchDarkly();
  const nodeData = data as NodeData;
  const [showToolbar, setShowToolbar] = useState(false);
  const prevResultRef = useRef<typeof nodeData.result>(undefined);
  const isNodeLocked = Boolean(data.isLocked) || Boolean(data.isWorkflowLocked);

  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const isDropTarget = useNodeStore((state) => state.dragOverNodeId === id);
  const {
    onDuplicateNode,
    onNodesEdgesDelete,
    onCopyNode,
    handleParameterChange: storeHandleParameterChange,
    updateNodeAttributes,
  } = useNodeStore();

  const {
    struct,
    result,
    nodeStatusEvents = [],
    isConnected = false,
    isLoading = false,
    isExecuting = false,
    hasError = false,
    isCompleted = false,
    isCached = false,
    isPausedWaiting = false,
    isRetrying = false,
    isRerunnable = false,
    // progressPercent,
    statusMessage,
  } = nodeData || {};

  const groupedCatalogNodes = useNodeStore((state) => state.groupedNodes);
  const catalogTypeName = useMemo(() => {
    if (!struct?.node_id) return undefined;
    return (
      findCatalogStruct(groupedCatalogNodes, struct.node_id)?.display_name ??
      struct.display_name
    );
  }, [groupedCatalogNodes, struct?.node_id, struct?.display_name]);

  // ── Retry context for the amber Retrying badge ────────────────────
  //
  // The latest RETRY_SCHEDULED event (if any) carries enough payload
  // for a useful tooltip: attempt N of M, the user-friendly reason
  // ("rate limit", "timeout"), and an optional retry-in-seconds.
  // Pulled out here so the JSX below stays a clean ternary cascade.
  let retryAttemptText: string | undefined;
  let retryReasonText: string | undefined;
  if (isRetrying) {
    // Walk backwards to find the most recent retry event -- cheap
    // because retry events are rare relative to streaming events.
    for (let i = nodeStatusEvents.length - 1; i >= 0; i--) {
      const e = nodeStatusEvents[i] as {
        event_type?: string;
        data?: {
          attempt?: number;
          max_attempts?: number;
          user_message?: string;
        };
      };
      if (e.event_type === 'retry_scheduled' && e.data) {
        const a = e.data.attempt;
        const m = e.data.max_attempts;
        if (typeof a === 'number' && typeof m === 'number' && m > 0) {
          retryAttemptText = `Retrying — attempt ${a + 1} of ${m}`;
        } else if (typeof a === 'number') {
          retryAttemptText = `Retrying — attempt ${a + 1}`;
        } else {
          retryAttemptText = 'Retrying...';
        }
        if (typeof e.data.user_message === 'string') {
          retryReasonText = e.data.user_message;
        }
        break;
      }
    }
  }

  // Editor-scoped per-node actions (currently just the play button
  // handler).  Read via context rather than data so callback
  // identity changes don't trigger ReactFlow re-renders.
  const { onRerunFromHere } = useNodeActions();
  const handleRerunClick = useCallback(
    (e: React.MouseEvent) => {
      // Stop the click from bubbling into ReactFlow's selection
      // handler -- otherwise clicking play would also re-select
      // the node, which is noisy.
      e.stopPropagation();
      e.preventDefault();
      if (!onRerunFromHere) return;
      onRerunFromHere(id);
    },
    [onRerunFromHere, id],
  );

  const handleParameterChange = useCallback(
    (fieldId: string, value: InputValue, key?: string) => {
      storeHandleParameterChange(id, fieldId, value, key);
    },
    [storeHandleParameterChange, id],
  );

  // Find file upload field for file_input nodes
  const fileUploadField = struct?.params?.find(
    (p) => p.display_modal === NodeInputType.FILE_UPLOAD,
  );

  // Find text input field for text_input nodes
  const textInputField = struct?.params?.find(
    (p) => p.display_modal === NodeInputType.TEXT_AREA,
  );

  const handleOpenPreview = useCallback(() => {
    showPreview({
      result: result as Record<string, InputValue> | null | undefined,
      nodeId: struct.node_id,
      reactFlowGeneratedId: struct.react_flow_generated_id,
      isLoading: Boolean(isLoading),
      icon: struct.icon,
      displayName: struct.display_name,
      nodeType: struct.node_type,
    });
  }, [
    showPreview,
    result,
    isLoading,
    struct.icon,
    struct.display_name,
    struct.node_type,
    struct.node_id,
    struct.react_flow_generated_id,
  ]);

  /**
   * Handle opening the files preview panel.
   * Fetches fresh presigned URLs for all files using S3 metadata.
   * Works for both persisted files and live execution files.
   */
  const handleOpenFilesPreview = useCallback(async () => {
    // Show panel immediately with loading state
    showFilesPreview({
      files: result?.files || [],
      displayName: struct.display_name,
      icon: struct.icon,
      nodeType: struct.node_type,
      isLoading: true,
      error: null,
    });

    if (!currentOrg?.orgid) {
      // No org, show with existing URLs
      updateFilesPreview({ isLoading: false });
      return;
    }

    try {
      const freshFiles = await refreshFileUrls(
        currentOrg.orgid,
        result?.files || [],
      );
      updateFilesPreview({
        files: freshFiles,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Failed to refresh file URLs:', error);
      Sentry.captureException(error, {
        extra: { context: 'refresh-presigned-urls', orgId: currentOrg?.orgid },
      });
      updateFilesPreview({
        isLoading: false,
        error: 'Failed to load fresh file URLs. Files may have expired.',
      });
    }
  }, [
    result?.files,
    showFilesPreview,
    updateFilesPreview,
    currentOrg?.orgid,
    struct.display_name,
    struct.icon,
    struct.node_type,
  ]);

  // Check if this is a Human Intervention node
  const isHumanInterventionNode =
    struct?.node_sub_type === 'human_intervention';
  const isLiveTranscriptionNode =
    struct?.node_sub_type === 'live_audio_transcription';
  const hasLiveTranscript = nodeStatusEvents.some(
    (event) => event.event_type === WorkflowEventTypes.LIVE_TRANSCRIPT,
  );

  // Helper function to parse event data array format
  const parseEventDataArray = (data: unknown): Record<string, unknown> => {
    if (!data) return {};

    // If it's already an object, return it
    if (typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, unknown>;
    }

    // If it's an array of [key, value] pairs, convert to object
    if (Array.isArray(data)) {
      const result: Record<string, unknown> = {};
      for (const item of data) {
        if (Array.isArray(item) && item.length >= 2) {
          const [key, value] = item;
          result[key] = value;
        }
      }
      return result;
    }

    return {};
  };

  // True when this node's result is exposed as an on-canvas artifact card
  // (file or markdown/text). The card replaces the floating control bar, so
  // the bar should not auto-show.
  const hasArtifact = useMemo(
    () =>
      resultProducesArtifact(
        result as NodeResult | undefined,
        struct?.node_sub_type,
      ),
    [result, struct?.node_sub_type],
  );

  // Detect when node receives new output and auto-show the floating control bar
  useEffect(() => {
    const shouldAutoShowToolbar =
      struct?.node_type === NodeCategory.GOVERNANCE ||
      struct?.node_type === NodeCategory.OUTPUT ||
      struct?.node_type === NodeCategory.AGENT;

    if (!prevResultRef.current && result && shouldAutoShowToolbar) {
      if (!hasArtifact) {
        setShowToolbar(true);
      }
    }
    prevResultRef.current = result;
  }, [result, struct?.node_type, hasArtifact]);

  // Clear showToolbar when node becomes selected (user is interacting with it)
  useEffect(() => {
    if (isSelected && showToolbar) {
      setShowToolbar(false);
    }
  }, [isSelected, showToolbar]);

  // Check for AWAITING_INTERVENTION status in nodeStatusEvents
  useEffect(() => {
    if (
      isHumanInterventionNode &&
      nodeStatusEvents &&
      nodeStatusEvents.length > 0
    ) {
      const latestEvent = nodeStatusEvents[nodeStatusEvents.length - 1];
      if (
        'event_type' in latestEvent &&
        latestEvent.event_type === NodeEventType.AWAITING_INTERVENTION
      ) {
        // Parse the event data to extract request_id and timeout
        const parsedData = parseEventDataArray(latestEvent.data);

        const requestId = parsedData.request_id
          ? (parsedData.request_id as string)
          : null;

        if (!requestId) {
          console.warn('No request_id found in event data');
          return;
        }

        // Extract and validate timeout
        let timeout = 300; // Default 300 seconds (5 minutes)
        if (parsedData.timeout != null) {
          const parsedTimeout = Number(parsedData.timeout);
          if (Number.isFinite(parsedTimeout) && parsedTimeout > 0) {
            timeout = parsedTimeout;
          } else {
            console.warn(
              'Invalid timeout value, using default:',
              parsedData.timeout,
            );
          }
        }

        // Anchor to the event timestamp so a rehydrated intervention shows
        // real remaining time, not a fresh full countdown.
        const eventTimestampMs =
          'timestamp' in latestEvent && latestEvent.timestamp
            ? new Date(latestEvent.timestamp).getTime()
            : NaN;
        const startMs = Number.isFinite(eventTimestampMs)
          ? eventTimestampMs
          : Date.now();
        const endTime = startMs + timeout * 1000;

        if (endTime <= Date.now()) {
          if (interventionState?.nodeId === id) {
            clearInterventionState();
          }
          return;
        }

        setInterventionState({
          isInterventionPending: true,
          interventionRequestId: requestId,
          nodeId: id,
          endTime,
          totalSeconds: timeout,
          nodeIcon: struct.icon,
          nodeIconColor: struct.icon_color,
          nodeType: struct.node_type,
        });
      } else if (
        'event_type' in latestEvent &&
        (latestEvent.event_type === NodeEventType.END ||
          latestEvent.event_type === NodeEventType.EXECUTION_SUCCESSFUL ||
          latestEvent.event_type === NodeEventType.EXECUTION_FAILED)
      ) {
        // Only clear intervention if THIS node owns the active intervention
        // Prevents other nodes from clearing the banner
        if (interventionState?.nodeId === id) {
          clearInterventionState();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isHumanInterventionNode,
    nodeStatusEvents,
    id,
    setInterventionState,
    clearInterventionState,
    struct.icon,
    struct.icon_color,
    struct.node_type,
    // Note: interventionState is intentionally NOT in deps - we only read it for conditional logic
    // We want to react to nodeStatusEvents changes, not interventionState changes
  ]);

  // Expiry is resolved by the backend, and the banner is retired by the
  // node events above; the reviewer's actions live on HumanInterventionBanner.
  // Only the owning node watches the connection, to avoid duplicate timers.
  useEffect(() => {
    // Only run for this specific node if it's the one with the intervention
    if (
      !interventionState ||
      interventionState.nodeId !== id ||
      interventionState.endTime === null
    ) {
      return;
    }

    // Check if websocket is disconnected, if so, clear the intervention state
    if (!websocketService.isConnected()) {
      clearInterventionState();
      return;
    }

    const interval = setInterval(() => {
      if (!websocketService.isConnected()) {
        clearInterventionState();
      }
    }, 500); // Check every 500ms for faster websocket disconnection detection

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    interventionState?.nodeId,
    interventionState?.endTime,
    id,
    clearInterventionState,
    // Note: intentionally not including full interventionState to avoid recreating interval on every store update
  ]);

  const handleOpenNodeStatus = useCallback(() => {
    openWorkflowPanel('events', {
      nodeId: struct.react_flow_generated_id || '',
      nodeData: {
        result: result as Record<string, InputValue> | null | undefined,
        isLoading: Boolean(isLoading),
        nodeStatusEvents: nodeStatusEvents || [],
      },
    });
    // The floating card is the new sidebar's home; pinning it would dock the
    // panel over the canvas buttons.
    if (isCanvasA2aChatEnabled) {
      setConsolePanelOpen(true);
    } else {
      setMaximized(true);
    }
    // Set the active tab to events to show the websocket status panel
    setActiveTab('events');
    // Filter the websocket status panel to show only this node's events
    setFilteredNodeId(struct.react_flow_generated_id || null);
  }, [
    openWorkflowPanel,
    isCanvasA2aChatEnabled,
    setConsolePanelOpen,
    setMaximized,
    setActiveTab,
    setFilteredNodeId,
    struct.react_flow_generated_id,
    result,
    isLoading,
    nodeStatusEvents,
  ]);

  // Update preview panel when data changes
  useEffect(() => {
    const { isPreviewVisible, previewData } = useWorkflowPanelStore.getState();
    // Only update if this node's preview is currently visible
    // Use struct.node_id for comparison since that's what we store in previewData.nodeId
    if (
      isPreviewVisible &&
      previewData &&
      previewData.nodeId === struct.node_id &&
      previewData.reactFlowGeneratedId === struct.react_flow_generated_id
    ) {
      showPreview({
        result: result as Record<string, InputValue> | null | undefined,
        nodeId: struct.node_id,
        reactFlowGeneratedId: struct.react_flow_generated_id,
        isLoading: Boolean(isLoading),
        icon: struct.icon,
        displayName: struct.display_name,
        nodeType: struct.node_type,
      });
    }
  }, [
    result,
    isLoading,
    struct.node_id,
    struct.react_flow_generated_id,
    struct.icon,
    struct.display_name,
    showPreview,
    struct.node_type,
  ]);

  // Determine if the node should blink
  const shouldBlink =
    isHumanInterventionNode &&
    interventionState?.isInterventionPending &&
    interventionState?.nodeId === id &&
    websocketService.isConnected();

  // Determine if action buttons (Output/Events) should be shown
  const shouldShowActionButtons = struct.node_type !== NodeCategory.TOOL;
  const enableEventsButton = nodeStatusEvents && nodeStatusEvents.length > 0;
  const noInputHandles =
    struct.input_ports.length === 0 &&
    struct.params.filter(
      (field) =>
        !field.advanced &&
        field.bindable_config &&
        !INLINE_COLLECTION_FIELD_IDS.has(field.field_id),
    ).length === 0;

  // Check if this node has validation errors that should show node-level indicators
  const hasNodeLevelFieldErrors = fieldErrors.some((error) => {
    if (error.nodeId !== id) return false;

    // Find the param that matches this error
    const param = struct.params?.find(
      (p) => p.field_id === error.fieldId || p.display_name === error.fieldName,
    );

    // Exclude file_input node special fields - their errors show on PreviewSlotCard
    // Only applies to actual file_input nodes, not other nodes that happen to have FILE_UPLOAD fields
    if (
      param &&
      struct.node_sub_type === 'file_input' &&
      fileUploadField &&
      param.field_id === fileUploadField.field_id
    ) {
      return false;
    }

    // Exclude inline collection scope fields - their errors show on CollectionScopeCard
    if (param && INLINE_COLLECTION_FIELD_IDS.has(param.field_id)) {
      return false;
    }

    // Show node-level error for non-bindable fields AND bindable fields that aren't bound
    // (bound fields are skipped during validation, so any error here means the field needs attention)
    return param && (!param.bindable_config || !param.bindable_config.is_bound);
  });

  // Check if this is a single handle node (for group hover behavior)
  const isSingleHandleNode =
    struct.output_ports.length === 1 &&
    Boolean(noInputHandles) &&
    struct.node_sub_type !== 'file_input' &&
    struct.node_sub_type !== 'text_input';

  return (
    <div
      className={cn('function-node', {
        selected: isSelected,
        'workflow-running': isLoading || persistStatusHighlight,
        loading: isLoading,
        // ``executing`` styles the yellow outline / glow.  Suppressed
        // when the node is in a more specific in-flight state:
        //   * ``isPausedWaiting`` -- amber outline takes over.
        //   * ``isRetrying``      -- the orange retry badge alone is
        //                            the signal; yellow outline would
        //                            be a visually confusing
        //                            "executing normally" cue right
        //                            after a failed attempt.
        executing: isExecuting && !isPausedWaiting && !isRetrying,
        'paused-waiting': isPausedWaiting,
        completed: isCompleted,
        error: hasError,
        'awaiting-intervention': shouldBlink,
        'validation-error': hasNodeLevelFieldErrors,
        'disabled-node': isDisabledNode,
        'drop-target': isDropTarget,
        group: isSingleHandleNode,
      })}
    >
      {/* Floating Control Bar - Shown when node is selected OR has new output */}
      {((isSelected && selectedNodeIds.length === 1) ||
        (showToolbar && !hasArtifact)) && (
        <FloatingControlBar
          onCopyNode={onCopyNode}
          onDuplicateNode={onDuplicateNode}
          onNodesEdgesDelete={onNodesEdgesDelete}
          nodeId={id}
          onOpenPreview={handleOpenPreview}
          onOpenNodeStatus={handleOpenNodeStatus}
          onOpenFilesPreview={handleOpenFilesPreview}
          shouldShowViewButtons={Boolean(shouldShowActionButtons)}
          enableOutputButton={Boolean(result)}
          enableEventsButton={enableEventsButton}
          enableFilesPreview={Boolean(result?.files?.length)}
          isLocked={isNodeLocked}
          hideOutputButton={
            struct.node_sub_type === 'file_input' ||
            struct.node_sub_type === 'file_browser'
          }
          catalogTypeName={catalogTypeName}
          catalogNodeId={struct.node_id}
        />
      )}

      {/* Disabled Node Badge - Top Right Corner */}
      {isDisabledNode && !isExecuting && !hasError && !isCompleted && (
        <div
          className='absolute -top-2 -right-2 size-6 rounded-md bg-amber-500 border-2 border-amber-500 flex items-center justify-center shadow-md z-10'
          title='This node is disabled for your organization'
        >
          <TriangleAlert className='size-4 text-white' />
        </div>
      )}

      {/* Status Icon - Top Right Corner */}
      {isRetrying ? (
        // Transient failure on the previous attempt; the BE has
        // scheduled a retry per the activity's RetryPolicy.  Amber
        // spinner (orange-leaning, distinct from yellow "executing"
        // and from amber "paused") + tooltip showing attempt N of M
        // and the user-friendly reason from the RETRY_SCHEDULED
        // event payload.  Kept ABOVE hasError in this ternary so a
        // node that emit_error'd on the failed attempt still reads
        // as "retrying" to the user, not "failed".
        <div
          className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-orange-500 border-2 border-orange-500 flex items-center justify-center shadow-md z-10'
          title={
            retryReasonText
              ? `${retryAttemptText ?? 'Retrying...'} — ${retryReasonText}`
              : (retryAttemptText ?? 'Retrying...')
          }
        >
          <LoadingSpinner size='sm' className='text-white' />
        </div>
      ) : isPausedWaiting ? (
        // Workflow is paused AND this node was in flight when pause
        // hit -- the activity was cancelled abruptly and will
        // re-run on resume.  Amber square (matches the status bar's
        // PAUSED bar) so the user reads "waiting" rather than
        // "still running".
        <div
          className='absolute -top-2 -right-2 w-6 h-6 rounded-md bg-amber-500 border-2 border-amber-500 flex items-center justify-center shadow-md z-10'
          title='Cancelled mid-run by pause — will re-execute on resume'
        >
          <Pause className='size-3.5 text-white' strokeWidth={3} />
        </div>
      ) : isExecuting ? (
        <div
          className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-yellow-500 border-2 border-yellow-500 flex items-center justify-center shadow-md z-10'
          title={
            typeof statusMessage === 'string' ? statusMessage : 'Executing...'
          }
        >
          <LoadingSpinner size='sm' className='text-white' />
        </div>
      ) : hasError ? (
        <div
          className='absolute -top-2 -right-2 w-6 h-6 rounded-md bg-red-500 border-2 border-red-500 flex items-center justify-center shadow-md z-10'
          title={
            typeof statusMessage === 'string'
              ? statusMessage
              : 'Execution failed'
          }
        >
          <TriangleAlert className='w-4 h-4 text-white' />
        </div>
      ) : isCompleted && isCached ? (
        // Cached badge: distinct from the regular completed check
        // so the user can tell at a glance which nodes ran fresh
        // vs reused output from a previous run.  Teal Database icon
        // is the project's existing visual vocabulary for "from
        // storage" (also used in node-list / file panels).
        <div
          className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-teal-500 border-2 border-teal-500 flex items-center justify-center shadow-md z-10'
          title='Reused from a previous run'
        >
          <Database className='size-3.5 text-white' />
        </div>
      ) : isCompleted ? (
        <div
          className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-green-500 border-2 border-green-500 flex items-center justify-center shadow-md z-10'
          title={
            typeof statusMessage === 'string'
              ? statusMessage
              : 'Execution completed'
          }
        >
          <Check className='w-4 h-4 text-white' />
        </div>
      ) : isCached ? (
        // Cached but not yet "completed" by an execution event --
        // happens for the few seconds between rerun_seeded firing
        // and the run reaching the persist phase.  Same teal badge
        // so the FE never flickers between "no badge" and "cached".
        <div
          className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-teal-500 border-2 border-teal-500 flex items-center justify-center shadow-md z-10'
          title='Reused from a previous run'
        >
          <Database className='size-3.5 text-white' />
        </div>
      ) : null}

      {/*
        Re-run from this node — top-LEFT corner so it doesn't
        collide with the status badge cluster on top-right.
        Always visible while the node is rerunnable; the editor
        only flips ``isRerunnable=true`` in the right contexts
        (paused live workflow OR historical view of a past run)
        AND only on nodes with a recorded terminal result
        (success or failure).  See ``isRerunnable`` derivation in
        workflow-editor-page.tsx.
      */}
      {isRerunnable && onRerunFromHere && (
        <button
          type='button'
          onClick={handleRerunClick}
          className='node-rerun-button absolute -top-2 -left-2 w-6 h-6 rounded-full bg-blue-500 hover:bg-blue-600 border-2 border-blue-500 hover:border-blue-600 flex items-center justify-center shadow-md z-10 transition-colors cursor-pointer'
          title='Re-run from this node'
          aria-label='Re-run from this node'
        >
          {/*
            Filled triangle reads as "play" universally.  3px
            stroke + slight horizontal nudge centres the visual
            weight of the asymmetric icon inside the circle.
          */}
          <Play
            className='w-3 h-3 text-white translate-x-[1px]'
            fill='currentColor'
            strokeWidth={2}
          />
        </button>
      )}

      <NodeHeader
        struct={struct}
        onNodeUpdate={(updates) => updateNodeAttributes(id, updates)}
        isEditable={!isNodeLocked}
        updateAvailable={updateAvailable}
      />
      {isLiveTranscriptionNode && (
        <LiveTranscriptionPanel
          // The panel's reducer describes one run, but the node outlives
          // every run on the canvas. Without this key a rerun keeps the
          // previous run's deltas and its `checkpointSequence` high-water
          // mark, which then swallows the new run's lower sequences and
          // leaves the panel showing the old transcript forever.
          key={currentSession ?? 'idle'}
          orgId={currentOrg?.orgid}
          workflowId={currentWorkflow?.id}
          sessionId={currentSession ?? undefined}
          nodeId={id}
          events={nodeStatusEvents}
          isConnected={isConnected}
          enabled={
            isExecuting ||
            isRetrying ||
            isCompleted ||
            hasError ||
            hasLiveTranscript
          }
          // A pending retry only defers termination while the workflow is
          // still running -- `hasError` and `isRetrying` are mutually
          // exclusive (a later RETRY_SCHEDULED clears `hasError`), so
          // gating on `!isRetrying` never deferred a live run and instead
          // left a run that ended on a dangling RETRY_SCHEDULED polling
          // forever, including when opening it as a past run.
          isRunTerminal={isCompleted || hasError || !isLoading}
        />
      )}
      {/* Single Output port */}
      {isSingleHandleNode &&
        (() => {
          const port = struct.output_ports[0];
          const config = getCategoryConfig(
            struct.node_type,
            port.id,
            port.display_name,
            port.port_type?.accepted_types,
          );
          return (
            <NodeHandle
              port={{
                id: port.id,
                display_name: port.display_name,
                info: port.info,
                port_type: port.port_type,
              }}
              nodeId={id}
              direction='output'
              isSingleHandle={true}
              config={config}
            />
          );
        })()}

      {/* Body */}
      <div
        className='node-body__wrapper'
        style={{ paddingBottom: !noInputHandles ? '10px' : '0' }}
      >
        {/* Bindable params (with input ports) — excludes inline collection scope fields */}
        {struct.params
          .filter(
            (field) =>
              !field.advanced &&
              field.bindable_config &&
              !INLINE_COLLECTION_FIELD_IDS.has(field.field_id),
          )
          .map((field: NodeParam, index: number) => (
            <SlotCard
              key={`${id}-${field.field_id}-${index}`}
              nodeId={id}
              port={field.bindable_config.input_port}
              portDirection='input'
              edgeType={ConnectionType.PORT_TO_PARAM}
              title={field.display_name}
              paramField={field}
              isLocked={isNodeLocked}
            />
          ))}

        {/* Inline collection/dataset scope cards */}
        {struct.params
          .filter(
            (field) =>
              INLINE_COLLECTION_FIELD_IDS.has(field.field_id) && !field.hidden,
          )
          .map((field: NodeParam, index: number) => (
            <CollectionScopeCard
              key={`${id}-${field.field_id}-scope-${index}`}
              nodeId={id}
              field={field}
              handleParameterChange={handleParameterChange}
              isLocked={isNodeLocked}
            />
          ))}

        {/* Tools and Memory Fields - Only for Agent nodes */}
        {struct.input_ports.length > 0 ? (
          <>
            {struct.input_ports.map((port, index) => (
              <SlotCard
                key={`${id}-${port.id}-${index}`}
                nodeId={id}
                port={port}
                portDirection='input'
                edgeType={ConnectionType.PORT_TO_PORT}
                isLocked={isNodeLocked}
              />
            ))}
          </>
        ) : null}

        {/* Runtime capability chips: surface config-declared
            workflow-launch / notification capabilities that otherwise
            render nothing on the canvas. */}
        <AgentCapabilityChips struct={struct} />

        {/* Output ports */}
        {(!noInputHandles ||
          struct.node_sub_type === 'file_input' ||
          struct.node_sub_type === 'text_input') &&
        struct.output_ports.length > 0 ? (
          struct.node_sub_type === 'file_input' ? (
            // Special handling for file_input nodes - show PreviewSlotCard in file mode
            fileUploadField && struct.output_ports[0] ? (
              <PreviewSlotCard
                key={`${id}-file-upload`}
                nodeId={id}
                field={fileUploadField}
                outputPort={struct.output_ports[0]}
                workflowId={currentWorkflow?.id}
                handleParameterChange={(fieldId, value) =>
                  handleParameterChange(fieldId, value as InputValue)
                }
                mode='file'
                nodeName={struct.display_name}
                nodeType={struct.node_type}
                isLocked={Boolean(data.isLocked)}
              />
            ) : null
          ) : struct.node_sub_type === 'text_input' ? (
            // Special handling for text_input nodes - show PreviewSlotCard in text mode
            textInputField && struct.output_ports[0] ? (
              <PreviewSlotCard
                key={`${id}-text-input`}
                nodeId={id}
                field={textInputField}
                outputPort={struct.output_ports[0]}
                workflowId={currentWorkflow?.id}
                handleParameterChange={(fieldId, value) =>
                  handleParameterChange(fieldId, value as InputValue)
                }
                mode='text'
                nodeName={struct.display_name}
                nodeType={struct.node_type}
                isLocked={Boolean(data.isLocked)}
              />
            ) : null
          ) : (
            // Regular output ports
            <div className='mt-2'>
              {struct.output_ports.map((port, index) => (
                <SlotCard
                  key={`${id}-${port.id}-${index}-output`}
                  nodeId={id}
                  port={port}
                  portDirection='output'
                  edgeType={ConnectionType.PORT_TO_PARAM}
                  onOpenFilesPreview={handleOpenFilesPreview}
                  isLocked={isNodeLocked}
                />
              ))}
            </div>
          )
        ) : null}
      </div>

      {/* Validation Error Message - Floating below node */}
      {hasNodeLevelFieldErrors && (
        <div className='absolute -bottom-7 left-0 right-0 flex justify-center pointer-events-none z-10'>
          <div className='text-red-500 text-sm font-medium flex items-center gap-1'>
            <span>⚠</span>
            <span>Configure required field</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(GenericNode);
